/**
 * Applicant Form_Renderer — FormRenderer
 * (application-form-builder spec, task 23.1).
 *
 * The top-level applicant form. It composes the three shared, I/O-free pure
 * modules — never re-implementing their rules — to decide what to show, and
 * delegates validation to the exact code the server runs:
 *
 *   1. Phase gating (`phaseGating.ts#availablePages`): every Phase_1 page, plus
 *      Phase_2 pages only once `applicationStatus === APPROVED` (Req 16.2–16.4).
 *   2. Per-division visibility (`formStructure.ts#isFieldVisibleForDivision` /
 *      `division-registry/visibility.ts#isVisibleForDivision`): a page/field is
 *      shown only for the applicant's Division key, with null/empty meaning all
 *      active divisions (Req 4.2–4.5).
 *   3. Audience membership (`audienceVisibility.ts#isInAudience`): a page/field
 *      is shown only when its Audience_Set includes `APPLICANT` (Req 22) — the
 *      Effective_Visibility conjunction with the division decision.
 *
 * Surviving pages and fields are rendered in ascending `displayOrder` (Req 5.1).
 * The form's Zod schema comes from `deriveZodSchema(snapshot, divisionKey)`,
 * which calls the same `fieldValidation.ts` rules the Lambda runs, so client
 * validation equals server validation (Req 5 ↔ Req 7). React Hook Form drives
 * the controls via the Field_Registry through `FieldRenderer`.
 *
 * Inline validation messaging (task 23.2) is wired here: per-field error slots
 * and LONG_TEXT/FILE_UPLOAD affordances live in `FieldRenderer` / the registry,
 * and this component renders the accessible submission-block summary that, on a
 * submit attempt, names every unanswered required visible field (Req 5.3).
 *
 * Bound controls (task 23.3) are now wired: a `boundControls` adapter seam is
 * provided to the field tree so Division / Title-preferences / Document-slot
 * fields write their canonical entities (never the response `values` map) and
 * Contact/identity renders locked read-only. The save-as-draft / submit /
 * approval-lock orchestration (23.4) remains the container's job: the `footer`
 * render-prop receives the live form so 23.4 can drive `handleSubmit`. This
 * component renders the ordered, gated, validated field tree (custom + bound)
 * with inline messaging; persistence and submission are wired by its container.
 *
 * Validates: Requirements 4.2, 4.3, 4.4, 4.5, 5.1, 5.3, 16.2, 16.3, 16.4
 */

import * as React from 'react';
import { useForm, type UseFormReturn } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { StatusChip } from '@/components/ui/StatusChip';
import { RichHelpText } from '@/components/application-form/RichHelpText';
import {
  deriveZodSchema,
  type ApplicationResponseValues,
} from '@/lib/application-form/deriveZodSchema';

import { FieldRenderer } from './FieldRenderer';
import {
  FieldRegistryContext,
  type FileUploadAdapter,
} from './fieldRegistry';
import {
  BoundControlProvider,
  type BoundControlAdapter,
} from './boundControls';
import { SubmissionErrorSummary } from './SubmissionErrorSummary';
import { fieldLabelMap, summarizeBlockingErrors } from './validationMessaging';
import {
  availablePages,
  isFieldVisibleForDivision,
  isInAudience,
  isVisibleForDivision,
  SubmissionStatus,
  suppressSeasonFields,
  type Division,
  type FormFieldDef,
  type FormVersionSnapshot,
  type PageApprovalStatus,
  type SnapshotPage,
} from './pure';

/** The audience the applicant renderer projects for (Req 22). */
const APPLICANT_AUDIENCE = 'APPLICANT' as const;

export interface FormRendererProps {
  /** The active Form_Version snapshot the applicant is completing. */
  snapshot: FormVersionSnapshot;
  /** The competition the response belongs to (the bound writes' scope). */
  competitionId: string;
  /** The applicant's assigned Division key (drives per-division visibility). */
  divisionKey: string;
  /** The competition's Divisions; only `active` ones count for the all-divisions case. */
  activeDivisions: readonly Division[];
  /** The application-workflow status (gates Phase_2 pages — Req 16). */
  applicationStatus: SubmissionStatus;
  /**
   * Whether required-and-visible fields must be answered. `false` while editing
   * a draft (Req 6.1); defaults to `true` (the submit gate). The draft/submit
   * orchestration itself is task 23.4 — this only selects the schema mode.
   */
  enforceRequired?: boolean;
  /** Seed values for the controlled form (e.g. a resumed draft). */
  defaultValues?: ApplicationResponseValues;
  /** Render every control read-only (e.g. a fully approval-locked form). */
  disabled?: boolean;
  /**
   * Optional per-field read-only predicate, layered on top of `disabled`. The
   * container (task 23.4) uses it to lock exactly the fields that must not
   * change without re-rendering the whole form read-only: an Immutable_Field
   * once the response is SUBMITTED (Req 6.4), and every field on an APPROVED
   * approval-required page (`isPageEditableByApplicant === false`, Req 21.4). A
   * field is read-only when `disabled` is set OR this returns `true` for it.
   */
  isFieldDisabled?: (field: FormFieldDef, page: SnapshotPage) => boolean;
  /**
   * The applicant's per-page approval statuses, keyed by `formPageId` (Req 21).
   * When an approval-required page (`requiresDirectorApproval`) is APPROVED for
   * this applicant, its page card shows an "Approved" pill (matching the admin
   * review StatusChip). A missing entry is the PENDING default (no pill). The
   * read-only locking of the page's fields is driven separately by the
   * container via `isFieldDisabled`; this prop is only the visible indicator.
   */
  pageApprovals?: Readonly<Record<string, PageApprovalStatus>>;
  /** Optional FILE_UPLOAD capture adapter (wired by a later task). */
  fileUploadAdapter?: FileUploadAdapter;
  /**
   * Optional bound-write seam (task 23.3): routes Division / Title-preferences /
   * Document-slot writes to their canonical entities and supplies the resolved
   * Contact/identity + document-catalog read-models the bound controls display.
   * Absent ⇒ bound controls render read-only/disabled. None of these writes
   * touch the response `values` map (Req 17.2).
   */
  boundControls?: BoundControlAdapter;
  /**
   * Optional render-prop footer (e.g. the draft/submit actions from task 23.4),
   * given the live React Hook Form instance so the container can read values and
   * drive `handleSubmit`. A failed submit populates the form errors, which the
   * renderer surfaces in its accessible submission-block summary (Req 5.3).
   */
  footer?: (form: UseFormReturn<ApplicationResponseValues>) => React.ReactNode;
  /**
   * Optional render-prop header rendered at the very top of the form, ABOVE the
   * dynamic sections, given the live React Hook Form instance so a consumer can
   * subscribe to `form.watch()` (application-completion-tracker §C3: the
   * contestant CompletionBar). Absent ⇒ nothing is rendered above the sections.
   */
  header?: (form: UseFormReturn<ApplicationResponseValues>) => React.ReactNode;
}

/** A page that survived phase + division + audience gating, with its visible fields. */
interface VisiblePage {
  page: SnapshotPage;
  fields: FormFieldDef[];
}

/** Ascending page `displayOrder` comparator (Req 5.1). */
function byPageDisplayOrder(a: SnapshotPage, b: SnapshotPage): number {
  return a.displayOrder - b.displayOrder;
}

/** Shared class for the page-level help text (matches the prior inline render). */
const PAGE_HELP_CLASS = 'text-body-sm text-muted-foreground';

/**
 * Whether a page carries any renderable help content (rich or plain). Mirrors
 * the truthiness `RichHelpText` itself guards on, so an empty surface renders
 * nothing (Req 7.1's "non-empty" precondition, P8.6).
 */
function hasPageHelpContent(page: SnapshotPage): boolean {
  const hasHtml = page.helpTextFormat === 'HTML' && Boolean(page.helpTextHtml);
  return hasHtml || Boolean(page.helpText);
}

/**
 * Page-level help disclosure (application-builder-rich-help-text spec, task 10.1;
 * Req 7). Two branches keyed off `autoCollapseHelp`:
 *
 *   - `autoCollapseHelp !== true` ⇒ the help renders expanded through the shared
 *     `RichHelpText`, with NO control — byte-for-byte the pre-feature behavior
 *     (Req 7.5, P8.6).
 *   - `autoCollapseHelp === true` AND the help is non-empty ⇒ an accessible
 *     `Collapse_Control` (a real `<button>` with `aria-expanded` + `aria-controls`
 *     and an accessible name) renders collapsed by default (Req 7.1). Toggling
 *     reveals/hides the region (Req 7.2, 7.3), whose content renders through the
 *     same `RichHelpText` so rich formatting is preserved when expanded (Req 7.6).
 *     Auto-collapse ON but empty help ⇒ nothing renders (Req 7.1 precondition).
 *
 * The disclosure state is LOCAL component state — visibility only, it never
 * mutates the help content (P8.5). The region is always mounted with a stable
 * `id` and hidden via the `hidden` attribute when collapsed, so `aria-controls`
 * always resolves to a real element (Req 7.4) and the revealed content equals
 * the content the non-collapsing branch renders.
 */
function PageHelp({ page }: { page: SnapshotPage }): React.ReactElement | null {
  const [open, setOpen] = React.useState(false);

  // Non-auto-collapse (or legacy absent flag): expanded, no control (Req 7.5).
  if (page.autoCollapseHelp !== true) {
    return (
      <RichHelpText
        format={page.helpTextFormat}
        helpText={page.helpText}
        helpTextHtml={page.helpTextHtml}
        className={PAGE_HELP_CLASS}
      />
    );
  }

  // Auto-collapse ON but no content ⇒ render nothing (Req 7.1 precondition).
  if (!hasPageHelpContent(page)) {
    return null;
  }

  const regionId = `${page.id}-help-region`;
  return (
    <div className="space-y-2">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={regionId}
        onClick={() => setOpen((prev) => !prev)}
        className="inline-flex items-center gap-1 text-body-sm font-medium text-primary hover:underline"
      >
        <span aria-hidden="true">{open ? '▾' : '▸'}</span>
        {open ? 'Hide section help' : 'Show section help'}
      </button>
      <div id={regionId} hidden={!open}>
        <RichHelpText
          format={page.helpTextFormat}
          helpText={page.helpText}
          helpTextHtml={page.helpTextHtml}
          className={PAGE_HELP_CLASS}
        />
      </div>
    </div>
  );
}

/**
 * Compute the pages (and their fields) visible to this applicant, in ascending
 * display order. Phase gating runs first, then per-page and per-field
 * Effective_Visibility (division-scope AND `APPLICANT` audience). A page with no
 * visible fields is dropped.
 *
 * Pages are emitted in ascending `displayOrder` (Req 5.1); a snapshot's fields
 * are already captured in ascending field order by `snapshotForm` (Property 3),
 * so their array order is preserved as-is.
 */
function computeVisiblePages(
  snapshot: FormVersionSnapshot,
  divisionKey: string,
  activeDivisions: readonly Division[],
  applicationStatus: SubmissionStatus,
): VisiblePage[] {
  // 1. Phase gate (Req 16.2–16.4).
  const phasePages = availablePages(snapshot.pages, applicationStatus);

  const visible: VisiblePage[] = [];

  for (const page of [...phasePages].sort(byPageDisplayOrder)) {
    // 2 + 3. Page Effective_Visibility: division-scope AND applicant audience.
    const pageDivisionVisible = isVisibleForDivision(
      page.visibleDivisionKeys,
      divisionKey,
      activeDivisions,
    );
    const pageVisible = pageDivisionVisible && isInAudience(page.audienceSet, APPLICANT_AUDIENCE);
    if (!pageVisible) {
      continue;
    }

    const pageVis = { visibleDivisionKeys: page.visibleDivisionKeys ?? null };

    // Applicant field-mapping seam: the Season is auto-derived and never an
    // applicant-facing selection, so a director-authored Season_Bound_Field
    // (`bindingTarget === 'SEASON'`) is dropped before any field is mapped to a
    // control here. `SeasonBoundControl` and every Director_Surface are untouched
    // — only this applicant renderer suppresses the field
    // (apply-season-auto-derivation Req 5.1, 5.2).
    const fields = suppressSeasonFields(page.fields).filter((field) => {
      const fieldVis = { visibleDivisionKeys: field.visibleDivisionKeys ?? null };
      const fieldDivisionVisible = isFieldVisibleForDivision(
        pageVis,
        fieldVis,
        divisionKey,
        activeDivisions,
      );
      return fieldDivisionVisible && isInAudience(field.audienceSet, APPLICANT_AUDIENCE);
    });

    if (fields.length > 0) {
      visible.push({ page, fields });
    }
  }

  return visible;
}

export function FormRenderer({
  snapshot,
  competitionId,
  divisionKey,
  activeDivisions,
  applicationStatus,
  enforceRequired = true,
  defaultValues,
  disabled,
  isFieldDisabled,
  pageApprovals,
  fileUploadAdapter,
  boundControls,
  footer,
  header,
}: FormRendererProps): React.ReactElement {
  // The schema is the same `fieldValidation.ts` the server runs (Req 5 ↔ 7);
  // recompute it only when the snapshot, division, or mode changes.
  const schema = React.useMemo(
    () => deriveZodSchema(snapshot, divisionKey, { enforceRequired }),
    [snapshot, divisionKey, enforceRequired],
  );

  const form = useForm<ApplicationResponseValues>({
    resolver: zodResolver(schema),
    defaultValues: defaultValues ?? {},
    mode: 'onBlur',
  });

  const { control, formState } = form;

  const visiblePages = React.useMemo(
    () => computeVisiblePages(snapshot, divisionKey, activeDivisions, applicationStatus),
    [snapshot, divisionKey, activeDivisions, applicationStatus],
  );

  const registryValue = React.useMemo(() => ({ fileUploadAdapter }), [fileUploadAdapter]);

  // The bound-control scope + write seam. Bound controls read/write their
  // canonical entity and never the response `values` map (Req 17.2); the seam's
  // method signatures map one-to-one onto the `applicationFormClient` transport.
  const boundControlValue = React.useMemo(
    () => ({ competitionId, divisionKey, activeDivisions, adapter: boundControls }),
    [competitionId, divisionKey, activeDivisions, boundControls],
  );

  // The submission-block summary lists the fields a submit attempt would reject —
  // most importantly every unanswered required visible field (Req 5.3). It is
  // derived from the React Hook Form errors (produced by the shared
  // `deriveZodSchema` / `validateResponse`) and shown only once a submit has been
  // attempted, so it does not flag fields the applicant has not yet reached.
  const labelMap = React.useMemo(() => fieldLabelMap(snapshot), [snapshot]);
  const summaryItems = React.useMemo(
    () =>
      formState.submitCount > 0
        ? summarizeBlockingErrors(
            formState.errors as Record<string, { message?: string } | undefined>,
            labelMap,
          )
        : [],
    [formState.submitCount, formState.errors, labelMap],
  );

  return (
    <FieldRegistryContext.Provider value={registryValue}>
      <BoundControlProvider value={boundControlValue}>
        <form className="space-y-8" noValidate>
          {header ? header(form) : null}

          <SubmissionErrorSummary items={summaryItems} focusToken={formState.submitCount} />

          {visiblePages.length === 0 ? (
            <p className="text-body text-muted-foreground" role="status">
              There are no application sections to complete yet.
            </p>
          ) : (
            visiblePages.map(({ page, fields }) => {
              // After approval, the Title_Preferences page is settled: the
              // ranked editor becomes a single read-only Title (handled in the
              // bound control), so its card heading is relabeled "Title" and the
              // "rank your preferences" help text is dropped.
              const isSettledTitlePage =
                applicationStatus === SubmissionStatus.APPROVED &&
                fields.some((f) => f.bindingTarget === 'TITLE_PREFERENCES');
              const cardTitle = isSettledTitlePage ? 'Approved Title' : page.title;
              // An approval-required page that the director has APPROVED for this
              // applicant shows an "Approved" pill in the heading row, matching the
              // admin review's StatusChip so the contestant sees the same indicator
              // as the director (Req 21). A missing status is the PENDING default.
              const showApprovedPill =
                page.requiresDirectorApproval && pageApprovals?.[page.id] === 'APPROVED';
              return (
              <Card key={page.id} role="group" aria-labelledby={`${page.id}-heading`}>
                <CardHeader>
                  <div className="flex items-start justify-between gap-3">
                    <CardTitle id={`${page.id}-heading`} className="font-display text-page-title-sm">
                      {cardTitle}
                    </CardTitle>
                    {showApprovedPill ? (
                      <div data-testid={`page-approved-pill-${page.id}`} className="shrink-0">
                        <StatusChip status="APPROVED" />
                      </div>
                    ) : null}
                  </div>
                  {isSettledTitlePage ? null : <PageHelp page={page} />}
                </CardHeader>
                <CardContent className="space-y-6">
                  {fields.map((field) => (
                    <FieldRenderer
                      key={field.id}
                      field={field}
                      control={control}
                      disabled={disabled || isFieldDisabled?.(field, page)}
                    />
                  ))}
                </CardContent>
              </Card>
              );
            })
          )}

          {footer ? footer(form) : null}
        </form>
      </BoundControlProvider>
    </FieldRegistryContext.Provider>
  );
}

export default FormRenderer;
